import find from 'lodash/find';
import uniqueId from 'lodash/uniqueId';
import _size from 'lodash/size';

import ErrorHandler from 'store/utils/ErrorHandler';
import Notify from 'store/utils/Notify';
import downloadFile from 'store/utils/downloadFile';
import {
  nameNewFile,
  getProgressPercentage,
  updateUploadingFiles,
  calculateDownloadedTestSetName,
  getNormalisedTestSets,
} from 'store/utils/fileSystem';
import { getRouterCurrentStateSelector } from 'store/services/router/selectors';
import { getRuleGroupsList } from 'store/services/ruleGroups/actions';
import { getTestSetEntriesList } from 'store/services/testSetEntries/actions';
import { DESELECT_RULE_MATCHED_FILTER_PARAMS } from 'store/services/ruleGroups/actionTypes';
import {
  getFiles as getFilesSelector,
  getUploadingFiles,
  getAllFiles,
  getPaginationOptions,
  getFilesMap,
} from './selectors';
import * as types from './actionTypes';
import * as api from './api';

const genId = () => uniqueId('test-set-');

/**
 * Get files
 */
export const getFiles = params => async (dispatch, getState) => {
  try {
    await dispatch({
      type: types.GET_TEST_SETS_FILES_REQUEST,
    });

    const state = getState();
    const { data } = await api.getFilesRequest(params);
    const filesMap = getFilesMap(state);
    const files = getNormalisedTestSets(data.results, filesMap);

    await dispatch({
      type: types.GET_TEST_SETS_FILES_SUCCESS,
      payload: {
        files,
        count: data.count,
        page: params?.page,
      },
    });
  } catch (error) {
    await dispatch({
      type: types.GET_TEST_SETS_FILES_ERROR,
      payload: {
        error: error.response.data,
      },
    });

    Notify.error({
      title: 'An error occurred',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Updated searchTerm
 */
export const updatedSearchTerm = searchTerm => ({
  type: types.UPDATE_TEST_SETS_SEARCH_TERM,
  payload: {
    searchTerm,
  },
});

/**
 * Get file
 *
 * @param file
 * @param isOpened - need this param in case updating current state for `openedAt`
 */
export const getFile = (file, isOpened = false) => async dispatch => {
  try {
    await dispatch({
      type: types.GET_TEST_SETS_FILE_REQUEST,
      payload: {
        id: file.id,
      },
    });
    const { data } = await api.downloadTestSetRequest(file.id);
    await dispatch({
      type: types.GET_TEST_SETS_FILE_SUCCESS,
      payload: {
        id: file.id,
        data,
        isOpened,
        openedAt: new Date().getTime(),
      },
    });
  } catch (error) {
    await dispatch({
      type: types.GET_TEST_SETS_FILE_ERROR,
      payload: {
        error: error.response.data,
      },
    });

    Notify.error({
      title: 'An error occurred',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

// eslint-disable-next-line
export const openFile = file => dispatch => {
  if (file && (file.contents || file.contents === '')) {
    dispatch({
      type: types.OPEN_TEST_SETS_FILE,
      payload: {
        id: file.id,
      },
    });

    return;
  }

  dispatch(getFile(file));
};

/**
 * Add file
 */
export const addFile = name => async dispatch => {
  try {
    await dispatch({
      type: types.ADD_TEST_SETS_FILE_REQUEST,
    });

    const { data } = await api.addFileRequest({ name });

    await dispatch({
      type: types.ADD_TEST_SETS_FILE_SUCCESS,
      payload: {
        file: data,
      },
    });

    await dispatch(openFile(data));

    Notify.success({
      title: 'Test set was created successfully',
    });
  } catch (error) {
    await dispatch({
      type: types.ADD_TEST_SETS_FILE_ERROR,
      payload: {
        error: error.response.data,
      },
    });

    Notify.error({
      title: 'An error occurred',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

export const uploadProgress = (file, dispatch, getState) => progress => {
  // DIRTY HACK: we set manually 90 as max percentage (and se manually to 100 when the file if fully uploaded)
  // to action UPLOAD_FILE_SUCCESS
  const percentage = Math.min(getProgressPercentage(progress), 90);
  const files = getUploadingFiles(getState());

  dispatch({
    type: types.UPLOAD_TEST_SETS_FILE_REQUEST,
    payload: {
      file,
      percentage,
      files,
    },
  });
};

/**
 * Upload a file and auto open it in editor and make it current.
 */
export const uploadFile = file => async (dispatch, getState) => {
  const stateFiles = getUploadingFiles(getState());
  try {
    // DIRTY HACK: set manually default progress percentage to 10 for better ux in cases of small files
    await dispatch({
      type: types.SET_TEST_SETS_DEFAULT_PERCENTAGE_TO_FILE,
      payload: {
        files: updateUploadingFiles(stateFiles, file, {
          percentage: 10,
          isLoading: true,
        }),
      },
    });

    const config = {
      onUploadProgress: await uploadProgress(file, dispatch, getState),
    };

    const response = await api.importTestSetRequest(file, config);
    const uploadedFile = response.data;

    const { data } = await api.downloadTestSetRequest(uploadedFile.id);

    await dispatch({
      type: types.UPLOAD_TEST_SETS_FILE_SUCCESS,
      payload: {
        ...uploadedFile,
        contents: data,
        openedAt: new Date().getTime(),
        // DIRTY HACK: when the file is fully uploaded we set manually percentage to 100
        uploadingFiles: updateUploadingFiles(stateFiles, file, {
          percentage: 100,
          isLoading: false,
          hasError: false,
        }),
      },
    });

    await dispatch(openFile(uploadedFile));
  } catch (error) {
    Notify.error({
      title: 'An error occurred',
      description: ErrorHandler.getNotificationError(error),
    });

    await dispatch({
      type: types.UPLOAD_TEST_SETS_FILE_ERROR,
      payload: {
        error: error.response.data,
        files: updateUploadingFiles(stateFiles, file, {
          isLoading: false,
          hasError: true,
          percentage: file.percentage,
        }),
      },
    });
  }
};

/**
 * Upload files
 */
export const uploadFiles = files => async (dispatch, getState) => {
  await dispatch({
    type: types.UPLOAD_TEST_SETS_FILES_REQUEST,
    files,
  });

  try {
    /* eslint-disable */
    for (const file of files) {
      await dispatch(uploadFile(file));
    }

    const stateFiles = getUploadingFiles(getState());
    const loadedFiles = stateFiles.filter(f => !f.hasError);

    if (loadedFiles.length === stateFiles.length) {
      await dispatch({
        type: types.UPLOAD_TEST_SETS_FILES_SUCCESS,
      });

      Notify.success({
        title: `${
          files.length === 1 ? 'Test Set was' : 'Test Sets were'
        } uploaded successfully`,
      });
    }
  } catch (error) {
    await dispatch({
      type: types.UPLOAD_TEST_SETS_FILES_ERROR,
      payload: {
        error: 'Error',
      },
    });
  }
};

/**
 * Update current files
 */
export const updateFiles = files => async (dispatch, getState) => {
  await Promise.all(
    files.map(file => {
      getFile(file, true)(dispatch, getState);
    }),
  );
};

export const resetUploadFiles = () => dispatch => {
  dispatch({
    type: types.RESET_TEST_SETS_UPLOAD_FILES,
  });
};

/**
 * Update file
 */
export const updateFile = (file, name) => async (dispatch, getState) => {
  try {
    await dispatch({
      type: types.UPDATE_TEST_SETS_FILE_REQUEST,
    });

    await api.updateFileRequest(file.id, { name });

    const files = getFilesSelector(getState()).map(entry => ({
      ...entry,
      name: entry.id === file.id ? name : entry.name,
    }));

    await dispatch({
      type: types.UPDATE_TEST_SETS_FILE_SUCCESS,
      payload: {
        files,
      },
    });

    Notify.success({ title: 'Test set was updated successfully' });
  } catch (error) {
    await dispatch({
      type: types.UPDATE_TEST_SETS_FILE_ERROR,
      payload: {
        error: error.response.data,
      },
    });

    Notify.error({
      title: 'An error occurred',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Add entry
 */
export const addNewFile = () => (dispatch, getState) => {
  const name = nameNewFile(getFilesSelector(getState()), 'New File');

  dispatch({
    type: types.ADD_TEST_SETS_NEW_FILE,
    payload: {
      file: {
        id: genId(),
        name,
        isNew: true,
      },
    },
  });
};

/**
 * Delete entry
 */
export const deleteFile = file => async (dispatch, getState) => {
  try {
    await dispatch({
      type: types.DELETE_TEST_SETS_FILE_REQUEST,
    });

    await api.deleteFileRequest(file.id);

    await dispatch({
      type: types.DELETE_TEST_SETS_FILE_SUCCESS,
      payload: {
        id: file.id,
      },
    });

    Notify.success({ title: 'Test set was deleted successfully' });
  } catch (error) {
    await dispatch({
      type: types.DELETE_TEST_SETS_FILE_ERROR,
      payload: {
        error: error.response.data,
      },
    });

    Notify.error({
      title: 'An error occurred',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Export test set
 */
export const exportFile = ({ id, name }) => async (dispatch, getState) => {
  try {
    dispatch({
      type: types.EXPORT_TEST_SETS_FILE_REQUEST,
    });

    const { data } = await api.downloadTestSetRequest(id);

    dispatch({
      type: types.EXPORT_TEST_SETS_FILE_SUCCESS,
    });

    downloadFile(data, calculateDownloadedTestSetName(name));
  } catch (error) {
    await dispatch({
      type: types.EXPORT_TEST_SETS_FILE_ERROR,
      payload: {
        error: error.response.data,
      },
    });

    Notify.error({
      title: 'An error occurred',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Update Blob's contents
 */
export const updateBlob = (file, value, snoozeNotification = false) => async (
  dispatch,
  getState,
) => {
  try {
    await dispatch({
      type: types.UPDATE_TEST_SETS_BLOB_REQUEST,
    });

    const blob = new Blob([value], { type: 'text/plain' });
    const data = new FormData();
    data.append('entries_file', blob);

    await api.replaceTestSetRequest(file.id, data);

    const files = getFilesSelector(getState());

    await dispatch({
      type: types.UPDATE_TEST_SETS_BLOB_SUCCESS,
      payload: {
        file,
        contents: value,
        files,
      },
    });

    if (!snoozeNotification) {
      Notify.success({
        title: 'Test Set was updated successfully',
      });
    }
  } catch (error) {
    await dispatch({
      type: types.UPDATE_TEST_SETS_BLOB_ERROR,
      payload: {
        error: error.response.data,
      },
    });

    Notify.error({
      title: 'An error occurred',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Close File
 */
export const closeEditorFile = ({ id }) => (dispatch, getState) => {
  const file = find(getAllFiles(getState()), { id });

  if (file && file.id) {
    return dispatch({
      type: types.CLOSE_TEST_SETS_FILE,
      payload: {
        id: file.id,
      },
    });
  }
};

/**
 * Update editor searchTerm
 */
export const updateEditorSearchTerm = searchTerm => ({
  type: types.UPDATE_TEST_SETS_EDITOR_SEARCH_TERM,
  payload: {
    searchTerm,
  },
});

/**
 * set theme
 */
export const setTheme = theme => ({
  type: types.SET_TEST_SETS_THEME,
  payload: {
    theme: theme,
  },
});

/**
 * Select a Test Sets
 *
 * @param testSet
 * @returns {Function}
 */
export const selectTestSet = testSet => async (dispatch, getState) => {
  await dispatch({
    type: types.SELECT_TEST_SET,
    payload: {
      testSet,
    },
  });

  await dispatch(getTestSetEntriesList({ page: 1 }));
  const name = getRouterCurrentStateSelector(getState());
  await dispatch({
    type: DESELECT_RULE_MATCHED_FILTER_PARAMS,
  });

  if (name.includes('groups')) {
    await dispatch(getRuleGroupsList());
  }
};

/**
 * Clear Test Sets selection
 *
 * @returns {{type: string}}
 */
export const deselectTestSet = () => async (dispatch, getState) => {
  await dispatch({
    type: types.DESELECT_TEST_SET,
  });

  const name = getRouterCurrentStateSelector(getState());
  await dispatch({
    type: DESELECT_RULE_MATCHED_FILTER_PARAMS,
  });

  if (name.includes('groups')) {
    await dispatch(getRuleGroupsList());
  }
};

/**
 * Get Test Sets
 */
export const getTestSetsList = (params = {}) => async (dispatch, getState) => {
  try {
    await dispatch({
      type: types.GET_TEST_SETS_LIST_REQUEST,
    });

    const state = getState();

    const finalParams = {
      ...getPaginationOptions(state),
      ...params,
    };

    await dispatch({
      type: types.UPDATE_TEST_SETS_PAGINATION_PARAMS,
      payload: {
        paginationOptions: finalParams,
      },
    });

    const {
      data: { results, count },
    } = await api.getFilesRequest(finalParams);

    await dispatch({
      type: types.GET_TEST_SETS_LIST_SUCCESS,
      payload: {
        testSets: results,
        count,
      },
    });
  } catch (error) {
    await dispatch({
      type: types.GET_TEST_SETS_LIST_ERROR,
      payload: {
        error: error.message,
      },
    });

    Notify.error({
      title: 'An error occurred',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Get Test Set
 *
 * @param id
 */
export const getTestSet = id => async (dispatch, getState) => {
  try {
    await dispatch({
      type: types.GET_TEST_SET_REQUEST,
    });

    const { data } = await api.getFileRequest(id);

    await dispatch({
      type: types.GET_TEST_SET_SUCCESS,
      payload: {
        testSet: data,
      },
    });
  } catch (error) {
    await dispatch({
      type: types.GET_TEST_SET_ERROR,
      payload: {
        error: error.message,
      },
    });

    Notify.error({
      title: 'An error occurred',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Delete multiple files
 */

export const deleteFiles = (files = []) => async (dispatch, getState) => {
  try {
    const deletedIds = files.map(file => file.id);

    dispatch({
      type: types.DELETE_TEST_SETS_FILES_REQUEST,
    });

    await api.deleteFilesRequest({
      delete: deletedIds,
    });

    dispatch({
      type: types.DELETE_TEST_SETS_FILES_SUCCESS,
      payload: {
        deletedIds,
      },
    });

    Notify.success({
      title: `Test ${
        _size(deletedIds) > 1 ? 'sets were' : 'set was'
      } deleted successfully`,
    });
  } catch (error) {
    dispatch({
      type: types.DELETE_TEST_SETS_FILES_ERROR,
      payload: {
        error: error.response.data,
      },
    });

    Notify.error({
      title: 'An error occurred',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Export multiple files
 */

export const exportFiles = (files = []) => async dispatch => {
  try {
    const ids = files.map(file => file.id);

    dispatch({
      type: types.EXPORT_TEST_SETS_FILES_REQUEST,
    });

    const { data } = await api.exportFilesRequest({
      ids,
    });

    downloadFile(data, 'testsets.zip');

    dispatch({
      type: types.EXPORT_TEST_SETS_FILES_SUCCESS,
    });
  } catch (error) {
    dispatch({
      type: types.EXPORT_TEST_SETS_FILES_ERROR,
      payload: {
        error: error.response.data,
      },
    });

    Notify.error({
      title: 'An error occurred',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Open files
 */
export const openFiles = (files = []) => async (dispatch, getState) => {
  dispatch({
    type: types.OPEN_TEST_SETS_FILES_REQUEST,
  });

  try {
    /* eslint-disable */
    for (const file of files) {
      await dispatch(openFile(file));
    }

    dispatch({
      type: types.OPEN_TEST_SETS_FILES_SUCCESS,
    });
  } catch (error) {
    dispatch({
      type: types.OPEN_TEST_SETS_FILES_ERROR,
      payload: {
        error: 'Error',
      },
    });
  }
};
